import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Box,
  Popper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

interface FaqSuggestion {
  _id: string;
  question: string;
  answer: string;
  programs?: { code: string; name: string }[];
  is_global: boolean;
}

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  loading?: boolean;
  suggestions: FaqSuggestion[];
  placeholder?: string;
  onSelectSuggestion?: (value: FaqSuggestion) => void;
  onEnterPress?: () => void;
  sx?: any;
}

const highlightMatch = (text: string, query: string) => {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.substring(0, idx)}
      <b style={{ color: "#111", fontWeight: 700 }}>
        {text.substring(idx, idx + query.length)}
      </b>
      {text.substring(idx + query.length)}
    </>
  );
};

const CustomPopper = (props: any) => (
  <Popper
    {...props}
    style={{
      ...props.style,
      maxHeight: 200,
      overflowY: "auto",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.08)",
      borderRadius: 8,
      background: "#fff",
    }}
    placement="bottom-start"
  />
);

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  loading = false,
  suggestions = [],
  placeholder = "Search...",
  onSelectSuggestion,
  onEnterPress,
  sx,
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Open dropdown if there are suggestions or loading, and there is input
    if (value && (suggestions.length > 0 || loading)) {
      setOpen(true);
    } else if (!value) {
      setOpen(false);
    }
  }, [suggestions, loading, value]);

  const handleInputChange = (_: any, newValue: string) => {
    const capitalized =
      newValue.length > 0
        ? newValue.charAt(0).toUpperCase() + newValue.slice(1)
        : "";
    onChange(capitalized);

    if (capitalized) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleOptionSelect = (
    _: any,
    selectedValue: string | FaqSuggestion | null,
    reason: string
  ) => {
    if (
      selectedValue &&
      typeof selectedValue !== "string" &&
      onSelectSuggestion &&
      reason === "selectOption"
    ) {
      onSelectSuggestion(selectedValue);
      setOpen(false); // Close dropdown after explicit selection
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent Autocomplete's default Enter behavior
      event.stopPropagation(); // Stop event from reaching Autocomplete
      if (onEnterPress) {
        onEnterPress(); // Trigger general search
      }
      setOpen(false); // Close dropdown on Enter
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={suggestions}
      loading={loading}
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      open={open}
      onOpen={() => value && setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionLabel={(option) => {
        const question = typeof option === "string" ? option : option.question;
        return question.charAt(0).toUpperCase() + question.slice(1);
      }}
      isOptionEqualToValue={(option, value) =>
        typeof option === "string"
          ? option === value
          : option._id === (typeof value === "string" ? value : value._id)
      }
      filterOptions={(x) => x} // Rely on external filtering
      sx={{ width: "100%", ...sx }}
      PopperComponent={CustomPopper}
      disableListWrap
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          onClick={(e) => {
            e.preventDefault();
            if (onSelectSuggestion) {
              onSelectSuggestion(option);
              setOpen(false); // Close dropdown on click
            }
          }}
          sx={{
            py: 2.5,
            px: 3,
            fontSize: 18,
            fontWeight: 400,
            border: 0,
            boxShadow: "none",
            listStyle: "none",
            cursor: "pointer",
            "&:hover": {
              background: "#f5f5f5",
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontSize: 18, fontWeight: 400, lineHeight: 1.4 }}
          >
            {highlightMatch(
              option.question.charAt(0).toUpperCase() +
                option.question.slice(1),
              value
            )}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          size="small"
          onKeyDown={handleKeyDown}
          sx={{
            borderRadius: 2,
            background: "#fafbfc",
            height: 48,
            "& .MuiOutlinedInput-root": {
              height: 48,
              borderRadius: 2,
              padding: 0,
            },
            "& .MuiInputBase-input": {
              height: 48,
              boxSizing: "border-box",
              padding: "0 0 0 14px",
              display: "flex",
              alignItems: "center",
              fontSize: 14,
              "&::placeholder": {
                fontSize: 14,
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ m: 0 }}>
                <SearchIcon sx={{ color: "#bdbdbd", fontSize: 24 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchBar;
